import React, { useState, useEffect } from 'react';
import { cn } from '../../../lib/utils';

const AgeInput = ({ 
  id = 'survey-age-input',
  value = '', 
  onChange, 
  onNext,
  placeholder = "Your age",
  className = '',
  disabled = false,
  required = false,
  min = 18,
  max = 120
}) => {
  const [age, setAge] = useState(value);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setAge(value);
  }, [value]);

  const validateAge = (ageValue) => {
    const numAge = Number(ageValue);
    
    if (!ageValue && required) {
      return 'Age is required';
    }

    if (ageValue && numAge < min) {
      return 'Antix users should be 18 or older.';
    }
    
    if (ageValue && (isNaN(numAge) || numAge < min || numAge > max)) {
      return `Please enter a valid age between ${min} and ${max}`;
    }
    
    return '';
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    
    // Allow only numbers
    if (newValue && !/^\d*$/.test(newValue)) {
      return;
    }
    
    // Limit to 3 digits
    if (newValue.length > 3) {
      return;
    }
    
    setAge(newValue);
    onChange?.(newValue);
    
    if (touched || newValue.length >= 2) {
      setTouched(true);
      const validationError = validateAge(newValue);
      setError(validationError);
    }
  };

  const handleBlur = () => {
    setTouched(true);
    const validationError = validateAge(age);
    setError(validationError);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && onNext) {
      e.preventDefault();
      setTouched(true);
      const validationError = validateAge(age);
      setError(validationError);
      if (age && !validationError) onNext();
    }
    // Prevent 'e', '+', '-' which are valid for number inputs
    if (['e', 'E', '+', '-', '.'].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          enterKeyHint={onNext ? 'next' : undefined}
          pattern="[0-9]*"
          name="survey-age"
          id={id}
          value={age}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={cn(
            "form-input",
            "pr-16 text-left text-lg font-medium",
            "h-[48px]", // Fixed height
            error && touched && "border-red-500/30",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          autoComplete="age"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          data-form-type="other"
          data-lpignore="true"
          aria-label="Age"
          aria-invalid={!!error && touched}
          aria-describedby={error && touched ? `${id}-error` : undefined}
        />
        {age && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <span className="text-sm text-white/50">
              {new Date().getFullYear() - parseInt(age)}
            </span>
          </div>
        )}
      </div>
      
      {error && touched && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

export default AgeInput;
